import { useState, useEffect, useCallback } from 'react';

// True on iPhone/iPad (incl. iPadOS 13+, which reports as "MacIntel" with touch points).
function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iPhoneIsh = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iPhoneIsh || iPadOS;
}

// True when already running as an installed PWA (standalone) — no install UI needed.
function detectStandalone() {
  if (typeof window === 'undefined') return false;
  const mm = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  return Boolean(mm || window.navigator.standalone === true);
}

/**
 * Install-prompt state for a PWA install affordance.
 *
 * Chrome fires `beforeinstallprompt` once, very early — usually before React mounts. An inline
 * script in index.html captures it onto `window.__mpgDeferredInstall` and emits `mpg:installavailable`.
 * This hook reads that global (so it works regardless of when the event fired) and stays subscribed.
 *
 * - canPrompt: a native install prompt is available (Android + desktop Chrome/Edge).
 * - isIOS: Safari has no prompt API — show manual "Add to Home Screen" instructions instead.
 * - isStandalone: already installed — hide install UI.
 */
export function useInstallPrompt() {
  const [canPrompt, setCanPrompt] = useState(
    () => typeof window !== 'undefined' && Boolean(window.__mpgDeferredInstall)
  );
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const isIOS = detectIOS();

  useEffect(() => {
    const onAvailable = () => setCanPrompt(Boolean(window.__mpgDeferredInstall));
    const onInstalled = () => {
      setCanPrompt(false);
      setIsStandalone(true);
    };
    window.addEventListener('mpg:installavailable', onAvailable);
    window.addEventListener('mpg:installed', onInstalled);
    // Re-sync in case the event fired between initial render and this effect.
    setCanPrompt(Boolean(window.__mpgDeferredInstall));
    return () => {
      window.removeEventListener('mpg:installavailable', onAvailable);
      window.removeEventListener('mpg:installed', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const evt = typeof window !== 'undefined' ? window.__mpgDeferredInstall : null;
    if (!evt) return false;
    evt.prompt();
    try {
      await evt.userChoice;
    } catch (_) {
      /* user dismissed / not relevant */
    }
    window.__mpgDeferredInstall = null; // a beforeinstallprompt event can only be used once
    setCanPrompt(false);
    return true;
  }, []);

  return {
    isStandalone,
    isIOS,
    canPrompt,
    promptInstall,
  };
}
